<template>
    <div class="home-layout">
      <div class="home-content">

        <!-- 右侧内容区 -->
        <main class="main-content" :class="{ shifted: isCollapsed }">
          <!-- 项目列表 -->
          <div class="project-list-container">
            <header class="list-header">
              <div class="list-title">
                项目列表 <span class="list-subtitle">({{ filteredProjectList.length }}/{{ projectList.length }})</span>
                <span v-if="projectSortBy" class="sort-indicator">
                  <el-tag size="small" type="primary">
                    {{ getSortLabel(projectSortBy) }}
                  </el-tag>
                </span>
              </div>
              <div class="list-tools">
                <el-select clearable
                  v-model="projectSortBy"
                  class="sort-select"
                  placeholder="排序"
                >
                  <el-option label="创建时间(新→旧)" value="createTime" />
                  <el-option label="创建时间(旧→新)" value="createTimeAsc" />
                  <el-option label="更新时间(新→旧)" value="updateTime" />
                  <el-option label="更新时间(旧→新)" value="updateTimeAsc" />
                  <el-option label="名称(A→Z)" value="name" />
                  <el-option label="名称(Z→A)" value="nameDesc" />
                </el-select>
                <el-button
                  v-if="projectSortBy"
                  size="small"
                  text
                  type="primary"
                  @click="clearSort"
                >
                  清除排序
                </el-button>
                <el-input
                  v-model="projectSearchText"
                  placeholder="搜索项目"
                  class="project-search"
                  clearable
                >
                  <template #prefix>
                    <el-icon><Search /></el-icon>
                  </template>
                </el-input>
              </div>
            </header>
            <div class="project-grid-scroll">
              <div class="project-grid">
                <div
                  v-for="project in filteredProjectList"
                  :key="project.id"
                  class="project-card"
                  @mouseenter="handleProjectHover(project.id)"
                  @mouseleave="handleProjectLeave(project.id)"
                  @click="handleProjectClick(project)"
                >
                  <div class="project-cover">
                    <el-image 
                      :src="project.imgPath || imgTemp" 
                      fit="cover"
                      class="cover-image"
                      :preview-src-list="[project.imgPath || imgTemp]"
                      :initial-index="0"
                      preview-teleported
                    >
                      <template #error>
                        <div class="cover-placeholder">
                          <el-icon><VideoCamera /></el-icon>
                        </div>
                      </template>
                    </el-image>
                    <div class="project-actions" v-show="hoveredProjectId === project.id">
                      <el-button
                        type="primary"
                        size="small"
                        text
                        icon="Edit"
                        @click.stop="handleRenameProject(project)"
                      >
                        重命名
                      </el-button>
                      <el-button
                        type="primary"
                        size="small"
                        text
                        icon="CopyDocument"
                        @click.stop="handleCopyProject(project)"
                      >
                        复制
                      </el-button>
                      <el-button
                        type="danger"
                        size="small"
                        text
                        icon="Delete"
                        @click.stop="handleDeleteProject(project)"
                      >
                        删除
                      </el-button>
                    </div>
                  </div>
                  <div class="project-info">
                    <div class="project-name">{{ project.projectName }}</div>
                    <div class="project-meta">
                      <div>创建: {{ formatDate(project.createDate) }}</div>
                      <div>
                        更新: {{ project.updateDate !== project.createDate ? formatDate(project.updateDate) : '-' }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card-new" @click="handleNewProject">
                  <el-icon :size="48" color="var(--accent-blue)"><Plus /></el-icon>
                  <span>创建项目</span>
                </div>
              </div>
            </div>
          
        </div>
        </main>
      </div>

      <!-- 重命名项目对话框 -->
      <el-dialog v-model="renameDialogVisible" title="重命名项目" width="300px" align-center>
        <el-form>
          <el-form-item label="项目名称">
            <el-input v-model="newProjectName" placeholder="请输入新的项目名称"></el-input>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="renameDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmRename">确定</el-button>
        </template>
      </el-dialog>

      <!-- 新建项目对话框 -->
      <el-dialog v-model="newProjectDialogVisible" title="新建项目" width="300px" align-center>
        <el-form>
          <el-form-item label="项目名称">
            <el-input v-model="newProjectNameInput" placeholder="请输入项目名称"></el-input>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="newProjectDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmNewProject">确定</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, computed, watch, nextTick} from 'vue'
  import { Plus, VideoCamera, Search } from '@element-plus/icons-vue'
  import { ElMessage, ElMessageBox } from 'element-plus'
  import { useRouter } from 'vue-router'

  //页面基础components start
  import Sidebar from '@/apps/portal/components/Sidebar.vue'
  import imgTemp from '@/apps/portal/assets/imgSnap.jpg'
  import { getProjectList, addProject, deleteProject, renameProject, copyProject } from '@/api/portal/projects'

  const TOOL_ID = 7

  // 用户信息
  const userPower = ref(0)
  const getUserInfo = () => {
    const userInfo = localStorage.getItem('userInfo')
    if (userInfo) {
      const parsedInfo = JSON.parse(userInfo)
      userPower.value = parsedInfo.totalAmount || 0
    } else {
      ElMessage.warning('获取用户信息失败')
    }
  }

  // 基础变量声明
  const router = useRouter()
  
  // Sidebar 相关状态
  const isCollapsed = ref(true) // 默认折叠
  const activeMenu = ref('i_14') // 默认选中"内容生产工具"
  
  

  onMounted(() => {
    try {
      // 设置页面标题
      document.title = '07视频批量混剪'
      
      // 检查登录状态
      const token = localStorage.getItem('token')
      if (!token) {
        ElMessage.warning('请先登录再使用该工具')
        // 使用nextTick确保组件完全渲染后再跳转
        nextTick(() => {
          router.push('/login')
        })
        return
      }
      getUserInfo()
      fetchProjectList() // 初始化项目列表
    } catch (error) {
      console.error('组件初始化失败:', error)
      ElMessage.error('组件初始化失败，请刷新页面重试')
    }
  })

  //页面基础components end

const projectList = ref([])

// 项目相关变量声明
const hoveredProjectId = ref(null)
const renameDialogVisible = ref(false)
const currentRenameProject = ref(null)
const newProjectName = ref('')
const newProjectDialogVisible = ref(false)
const newProjectNameInput = ref('')
const projectSearchText = ref('')
const projectSortBy = ref('createTime')


  // 监听排序变化
  watch(projectSortBy, (newSortBy, oldSortBy) => {
    if (newSortBy && newSortBy !== oldSortBy) {
      ElMessage.success(`已按${getSortLabel(newSortBy)}排序`)
    }
  })

  // 过滤和排序后的项目列表
  const filteredProjectList = computed(() => {
    let result = projectList.value
    
    // 搜索过滤
    if (projectSearchText.value.trim()) {
      result = result.filter(project => 
        project.projectName.toLowerCase().includes(projectSearchText.value.toLowerCase())
      )
    }
    
    // 排序
    if (projectSortBy.value) {
      result = [...result].sort((a, b) => {
        switch (projectSortBy.value) {
          case 'createTime':
            return new Date(b.createDate) - new Date(a.createDate)
          case 'updateTime':
            return new Date(b.updateDate) - new Date(a.updateDate)
          case 'name':
            return a.projectName.localeCompare(b.projectName, 'zh-CN')
          case 'nameDesc':
            return b.projectName.localeCompare(a.projectName, 'zh-CN')
          case 'createTimeAsc':
            return new Date(a.createDate) - new Date(b.createDate)
          case 'updateTimeAsc':
            return new Date(a.updateDate) - new Date(b.updateDate)
          default:
            return 0
        }
      })
    }
    
    return result
  })


 

  // 项目列表相关函数
  function handleProjectHover(projectId) {
    hoveredProjectId.value = projectId
  }

  function handleProjectLeave(projectId) {
    hoveredProjectId.value = null
  }

  // 添加防重复调用的标志
  let isProjectLoading = false

  function handleProjectClick(project) {
    console.log('=== 点击项目 ===')
    console.log('项目信息:', project)
    console.log('项目ID:', project.id)
    
    // 防止重复调用
    if (isProjectLoading) {
      console.log('项目正在加载中，忽略重复点击')
      return
    }
    
    isProjectLoading = true
    
    try {
      // 将项目名称写入缓存
      if (project.projectName) {
        localStorage.setItem('D07Edit_projectName', project.projectName)
        console.log('项目名称已写入缓存:', project.projectName)
      }
      
      // 在新标签页打开 D07Edit，项目列表保留在当前页面
      const target = router.resolve({
        path: '/detail/D07Edit',
        query: {
          projectId: project.id
        }
      })
      const editWindow = window.open(target.href, '_blank')
      if (editWindow) {
        editWindow.opener = null
      }
      
      console.log('已在新标签页打开D07Edit页面，项目:', project)
    } catch (error) {
      console.error('跳转失败:', error)
      ElMessage.error('跳转失败，请重试')
    } finally {
      // 加载完成后重置标志
      setTimeout(() => {
        isProjectLoading = false
      }, 100)
    }
  }



  function handleRenameProject(project) {
    currentRenameProject.value = project
    newProjectName.value = project.projectName
    renameDialogVisible.value = true
  }

  async function handleCopyProject(project) {
    try {
      const res = await copyProject(project.id)
      if (res && res.code === 0) {
        ElMessage.success('项目复制成功')
        // 重新获取项目列表
        await fetchProjectList()
      } else {
        ElMessage.error(res.msg || '复制失败')
      }
    } catch (error) {
      console.error('复制项目失败:', error)
      ElMessage.error('复制失败')
    }
  }

  async function handleDeleteProject(project) {
    try {
      await ElMessageBox.confirm(
        `确定要删除项目"${project.projectName}"吗？`,
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
      
      const res = await deleteProject(project.id)
      if (res && res.code === 0) {
        ElMessage.success('删除成功')
        // 重新获取项目列表
        await fetchProjectList()
      } else {
        ElMessage.error(res.msg || '删除失败')
      }
    } catch (error) {
      if (error !== 'cancel') {
        console.error('删除项目失败:', error)
        ElMessage.error('删除失败')
      }
    }
  }

  async function confirmRename() {
    if (!newProjectName.value.trim()) {
      ElMessage.warning('项目名称不能为空')
      return
    }
    
    if (currentRenameProject.value) {
      try {
        const res = await renameProject(currentRenameProject.value.id, newProjectName.value.trim())
        if (res && res.code === 0) {
          ElMessage.success('重命名成功')
          // 重新获取项目列表
          await fetchProjectList()
          renameDialogVisible.value = false
          currentRenameProject.value = null
          newProjectName.value = ''
        } else {
          ElMessage.error(res.msg || '重命名失败')
        }
      } catch (error) {
        console.error('重命名项目失败:', error)
        ElMessage.error('重命名失败')
      }
    }
  }

  function handleNewProject() {
    newProjectNameInput.value = ''
    newProjectDialogVisible.value = true
  }

  async function confirmNewProject() {
    if (!newProjectNameInput.value.trim()) {
      ElMessage.warning('项目名称不能为空')
      return
    }
    
    try {
      const res = await addProject(newProjectNameInput.value.trim(), TOOL_ID)
      if (res && res.code === 0) {
        ElMessage.success('项目创建成功')
        // 重新获取项目列表
        await fetchProjectList()
        newProjectDialogVisible.value = false
        newProjectNameInput.value = ''
      } else {
        ElMessage.error(res.msg || '创建失败')
      }
    } catch (error) {
      console.error('创建项目失败:', error)
      ElMessage.error('创建失败')
    }
  }

  // 获取项目列表
  async function fetchProjectList() {
    try {
      // 旧版 D07 项目的 toolId 为空，因此先取当前用户项目，再仅保留 D07 与历史空值数据。
      // 这样既不会混入 D63，也不会让已有的 D07 项目在升级后消失。
      const res = await getProjectList()
      if (res && res.code === 0 && res.list) {
        projectList.value = res.list.filter((project) => (
          project.toolId === null
          || project.toolId === undefined
          || Number(project.toolId) === TOOL_ID
        ))
      } else {
        console.error('获取项目列表失败:', res)
        projectList.value = []
      }
    } catch (error) {
      console.error('获取项目列表失败:', error)
      projectList.value = []
    }
  }




  // 格式化日期
  function formatDate(dateString) {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    } catch (error) {
      return dateString
    }
  }

  // 清除排序
  function clearSort() {
    projectSortBy.value = ''
  }

  // 获取排序标签
  function getSortLabel(sortBy) {
    const sortLabels = {
      'createTime': '按创建时间(新→旧)',
      'createTimeAsc': '按创建时间(旧→新)',
      'updateTime': '按更新时间(新→旧)',
      'updateTimeAsc': '按更新时间(旧→新)',
      'name': '按名称(A→Z)',
      'nameDesc': '按名称(Z→A)'
    }
    return sortLabels[sortBy] || '未知排序'
  }


</script>
  
  <style scoped>
  .home-layout {
    height: 100%;
    width: 100%;
    background-color: var(--bg-app);
    /* 背景氛围光斑 - 增加生命力 */
    background-image: 
    radial-gradient(circle at 10% 20%, rgba(147, 51, 234, 0.1) 0%, transparent 40%),
    radial-gradient(circle at 90% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 40%);
  }
  
  .home-content{
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    padding: 0;
    gap: 0;
    overflow: hidden;
    flex: 1;
    min-height: 0;
    box-sizing: border-box;
  }
  
  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 100%;
    width: 100%;
    height: 100%;
    background: #fff;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-float);
    border: 1px solid rgba(255, 255, 255, 0.5);
    overflow: hidden;
    box-sizing: border-box;
    position: relative;
    transition: margin-left 0.3s ease;
  }
  
  .main-content.shifted {
    margin-left: 0;
  }



  /* 项目列表样式 */
  .project-list-container {
    width: 100%;
    flex: 1;
    min-height: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .list-header {
    height: 28px;
    padding: 12px 24px;
    border-bottom: 1px solid #F3F4F6;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
  }

  .list-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary, #1F2937);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .list-subtitle {
    font-size: 14px;
    color: var(--text-secondary, #6B7280);
    font-weight: 400;
  }

  .sort-indicator {
    margin-left: 8px;
  }

  .list-tools {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .project-grid-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }

  .project-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 24px;
  }

  /* 项目卡片 */
  .project-card {
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    cursor: pointer;
    transition: all 0.3s;
  }

  .project-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    border-color: var(--accent-blue, #4F46E5);
  }

  .project-cover {
    height: 180px;
    background: #F3F4F6;
    position: relative;
    overflow: hidden;
  }

  .cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .cover-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #F3F4F6;
    color: #9CA3AF;
    font-size: 32px;
  }

  .project-cover .project-actions {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    gap: 8px;
    padding: 8px;
    background: rgba(255, 255, 255, 0.65);
    backdrop-filter: blur(8px);
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .project-card:hover .project-cover .project-actions {
    opacity: 1;
  }

  .project-info {
    padding: 16px 16px 0 16px;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .project-name {
    font-weight: 700;
    font-size: 16px;
    color: var(--text-primary, #1F2937);
    margin-bottom: 8px;
  }

  .project-meta {
    font-size: 12px;
    color: var(--text-secondary, #6B7280);
    line-height: 1.6;
    margin-bottom: 12px;
    flex: 1;
  }


  /* 新建卡片 */
  .card-new {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    border: 2px dashed #E5E7EB;
    background: #F9FAFB;
    border-radius: 12px;
    color: var(--text-secondary, #6B7280);
    cursor: pointer;
    transition: all 0.3s;
  }

  .card-new:hover {
    border-color: var(--accent-blue, #4F46E5);
    color: var(--accent-blue, #4F46E5);
    background: #EEF2FF;
  }

  .card-new span {
    margin-top: 16px;
    font-size: 14px;
    font-weight: 500;
  }

  .project-list-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-top: 1px solid #333;
    margin-top: 16px;
  }

  .footer-actions {
    display: flex;
    gap: 8px;
  }

  .footer-info {
    color: #999;
    font-size: 12px;
  }

  /* 与“自动化生产视频”统一的搜索区和项目卡片规范 */
  .project-list-container {
    padding: 30px 40px;
    background: var(--bg-panel, #fff);
  }

  .list-header {
    height: auto;
    min-height: 58px;
    padding: 14px 16px;
    margin-bottom: 16px;
    background: rgba(255, 255, 255, 0.78);
    border: 1px solid rgba(229, 231, 235, 0.9);
    border-radius: var(--radius-md, 16px);
    backdrop-filter: blur(10px);
    box-sizing: border-box;
  }

  .list-title {
    font-size: 16px;
  }

  .list-subtitle {
    padding: 4px 10px;
    font-size: 12px;
    border: 1px solid rgba(79, 70, 229, 0.15);
    border-radius: 999px;
    background: rgba(79, 70, 229, 0.06);
  }

  .list-tools {
    gap: 8px;
  }

  .sort-select {
    width: 160px;
  }

  .project-search {
    width: 320px;
  }

  .project-grid-scroll {
    padding: 12px 0 16px;
  }

  .project-grid {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 14px;
  }

  .project-card,
  .card-new {
    width: 240px;
    height: 300px;
    min-height: 300px;
    flex: 0 0 240px;
    border-radius: 16px;
    box-sizing: border-box;
  }

  .project-card {
    background: linear-gradient(
      180deg,
      rgba(79, 70, 229, 0.03) 0%,
      rgba(147, 51, 234, 0.015) 35%,
      rgba(255, 255, 255, 0.85) 100%
    );
    border: 1px solid rgba(229, 231, 235, 0.95);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .project-card:hover {
    transform: translateY(-5px);
    border-color: var(--accent-blue, #4f46e5);
    box-shadow: var(--shadow-hover, 0 10px 25px rgba(0, 0, 0, 0.08)), 0 0 0 1px rgba(79, 70, 229, 0.08);
  }

  .project-cover {
    height: 180px;
    border-radius: 16px 16px 0 0;
  }

  .project-info {
    padding: 16px;
  }

  .card-new {
    border: 1px dashed rgba(209, 213, 219, 0.95);
    background: var(--bg-card, #f9fafb);
    transition: all 0.3s ease;
  }

  .card-new:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 40px rgba(79, 70, 229, 0.12);
  }

  @media (max-width: 900px) {
    .project-list-container {
      padding: 20px;
    }

    .list-header {
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
    }

    .list-tools {
      width: 100%;
      flex-wrap: wrap;
    }

    .project-search {
      width: min(320px, 100%);
    }
  }

  </style>
